export class ZoudashUtils {
  // static class

  static isEqualPrimitive(a: unknown, b: unknown): boolean {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((v, i) => v === b[i]);
    }
    return a === b;
  }

  static flattenObject(obj: Record<string, unknown>, prefix = ''): Record<string, unknown> {
    return Object.entries(obj).reduce(
      (acc, [key, value]) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          Object.assign(acc, this.flattenObject(value as Record<string, unknown>, fullKey));
        } else {
          acc[fullKey] = value;
        }
        return acc;
      },
      {} as Record<string, unknown>,
    );
  }

  static isEqual(
    a: unknown,
    b: unknown,
    aStack = new WeakMap<object, unknown>(),
    bStack = new WeakMap<object, unknown>(),
  ): boolean {
    if (a === b) return true; // same reference or primitive
    if (a == null || b == null) return false; // null or undefined
    if (typeof a !== typeof b) return false;

    const isObjA = typeof a === 'object';
    const isObjB = typeof b === 'object';

    // Only use WeakMap tracking for objects (not primitives)
    if (isObjA && isObjB && a !== null && b !== null) {
      if (aStack.has(a as object)) return bStack.get(b as object) === a;
      aStack.set(a as object, b);
      bStack.set(b as object, a);
    }

    // Handle Dates
    if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();

    // Handle RegExp
    if (a instanceof RegExp && b instanceof RegExp) return a.source === b.source && a.flags === b.flags;

    // Handle File
    if (typeof File !== 'undefined' && a instanceof File && b instanceof File) {
      return a === b || (a.name === b.name && a.size === b.size && a.lastModified === b.lastModified);
    }

    // Handle Blob
    if (typeof Blob !== 'undefined' && a instanceof Blob && b instanceof Blob) {
      return a === b || (a.size === b.size && a.type === b.type);
    }

    // Handle Arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!this.isEqual(a[i], b[i], aStack, bStack)) return false;
      }
      return true;
    }

    // Handle Maps
    if (a instanceof Map && b instanceof Map) {
      if (a.size !== b.size) return false;
      for (const [key, val] of a) {
        if (!b.has(key) || !this.isEqual(val, b.get(key), aStack, bStack)) return false;
      }
      return true;
    }

    // Handle Sets
    if (a instanceof Set && b instanceof Set) {
      if (a.size !== b.size) return false;
      for (const val of a) {
        if (!b.has(val)) return false;
      }
      return true;
    }

    // Handle plain objects
    if (isObjA && isObjB && a !== null && b !== null) {
      const objA = a as Record<string, unknown>;
      const objB = b as Record<string, unknown>;
      const aKeys = Object.keys(objA);
      const bKeys = Object.keys(objB);
      if (aKeys.length !== bKeys.length) return false;

      for (const key of aKeys) {
        if (!Object.prototype.hasOwnProperty.call(objB, key)) return false;
        if (!this.isEqual(objA[key], objB[key], aStack, bStack)) return false;
      }
      return true;
    }

    // Fallback for other primitive or mismatched cases
    return false;
  }

  static deepClean<T>(obj: T): T {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) {
      return obj.map((v) => this.deepClean(v)) as T;
    }
    if (typeof obj === 'object') {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        if (value !== null) {
          acc[key as keyof T] = this.deepClean(value);
        }
        return acc;
      }, {} as T);
    }
    return obj;
  }
}
